import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/documentNode';
import * as Apollo from '@apollo/client';
export const OrganizationMembershipsDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"OrganizationMemberships"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"orgId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"organization"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"orgId"}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"memberships"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"memberType"}},{"kind":"Field","name":{"kind":"Name","value":"unconfirmed"}}]}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"OrganizationMemberships","document":OrganizationMembershipsDocument}} as const;
export type OrganizationMembershipsQueryVariables = Types.Exact<{
  orgId: Types.Scalars['ID']['input'];
}>;


export type OrganizationMembershipsQuery = (
  { __typename: 'Query' }
  & { organization?: Types.Maybe<(
    { __typename: 'Organization' }
    & Pick<Types.Organization, 'id'>
    & { memberships: Array<(
      { __typename: 'Organization_Membership' }
      & Pick<Types.Organization_Membership, 'id' | 'memberType' | 'unconfirmed'>
    )> }
  )> }
);

/**
 * __useOrganizationMembershipsQuery__
 *
 * To run a query within a React component, call `useOrganizationMembershipsQuery` and pass it any options that fit your needs.
 * When your component renders, `useOrganizationMembershipsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useOrganizationMembershipsQuery({
 *   variables: {
 *      orgId: // value for 'orgId'
 *   },
 * });
 */
export function useOrganizationMembershipsQuery(
  baseOptions: TrelloQueryHookOptions<
    OrganizationMembershipsQuery,
    OrganizationMembershipsQueryVariables
  > &
    (
      | { variables: OrganizationMembershipsQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: OrganizationMembershipsDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    OrganizationMembershipsQuery,
    OrganizationMembershipsQueryVariables
  >(OrganizationMembershipsDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function useOrganizationMembershipsLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    OrganizationMembershipsQuery,
    OrganizationMembershipsQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    OrganizationMembershipsQuery,
    OrganizationMembershipsQueryVariables
  >(OrganizationMembershipsDocument, options);
}
export function useOrganizationMembershipsSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        OrganizationMembershipsQuery,
        OrganizationMembershipsQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    OrganizationMembershipsQuery,
    OrganizationMembershipsQueryVariables
  >(OrganizationMembershipsDocument, options);
}
export type OrganizationMembershipsQueryHookResult = ReturnType<
  typeof useOrganizationMembershipsQuery
>;
export type OrganizationMembershipsLazyQueryHookResult = ReturnType<
  typeof useOrganizationMembershipsLazyQuery
>;
export type OrganizationMembershipsSuspenseQueryHookResult = ReturnType<
  typeof useOrganizationMembershipsSuspenseQuery
>;
export type OrganizationMembershipsQueryResult = Apollo.QueryResult<
  OrganizationMembershipsQuery,
  OrganizationMembershipsQueryVariables
>;
